import type {
  CourseMap,
  CourseMapSummary,
  CoursePath,
  CourseShape,
  HoleGeo,
  Landmark,
} from "../../shared/types.js";
import { getCourseMap, listCourseMaps } from "./http.js";
import { getMapCache, setMapCache } from "./storage.js";

const memoryCache = new Map<string, CourseMap>();

let manifest: CourseMapSummary[] | null = null;
let manifestFetch: Promise<CourseMapSummary[]> | null = null;

/**
 * The manifest, fetched at most once per page load.
 *
 * Failure is not an error state worth surfacing. A missing manifest means no distances
 * and no hole diagrams, which is exactly how the app behaves for an untraced course —
 * so it degrades into the normal case rather than into a broken one.
 */
async function loadManifest(): Promise<CourseMapSummary[]> {
  if (manifest) return manifest;
  if (!manifestFetch) {
    manifestFetch = listCourseMaps()
      .then((maps) => {
        manifest = maps;
        return maps;
      })
      .catch(() => {
        manifestFetch = null;
        return [];
      });
  }
  return manifestFetch;
}

/**
 * The map for a library course, or null.
 *
 * Exact id equality, deliberately. A course's name is never consulted — courses can be
 * created manually by anyone, so names collide by accident, and a "Highlands Course"
 * quietly inheriting an unrelated club's greens would put confidently wrong yardages in
 * front of a player. A map is attached to a course because an admin picked that course
 * in the builder, and for no other reason.
 *
 * A course whose map was traced before the course was deleted and re-added will miss
 * here, because the new course row has a new id. That is the intended outcome: the
 * builder lists the map as an orphan and offers to relink it in one click.
 */
export async function findMapForCourse(courseId: string): Promise<CourseMapSummary | null> {
  const maps = await loadManifest();
  return maps.find((m) => m.courseId === courseId) ?? null;
}

/**
 * Geometry for one map.
 *
 * Cached in localStorage against the blob SHA it came from: a hit on the same SHA is
 * always the right bytes, and a re-traced map changes SHA and refetches. Offline the
 * cached copy is used whatever its SHA, since stale geometry beats none when the reason
 * you want it is that you're standing in a field with no signal.
 */
export async function loadCourseMap(mapId: string, sha?: string): Promise<CourseMap | null> {
  const inMemory = memoryCache.get(mapId);
  if (inMemory) return inMemory;

  const cached = getMapCache(mapId);
  if (cached && (cached.sha === sha || navigator.onLine === false)) {
    memoryCache.set(mapId, cached.map);
    return cached.map;
  }

  try {
    const { map, sha: fetchedSha } = await getCourseMap(mapId, sha);
    memoryCache.set(mapId, map);
    setMapCache(map, fetchedSha);
    return map;
  } catch {
    return cached?.map ?? null;
  }
}

export interface HoleGeometry {
  geo: HoleGeo | null;
  /**
   * Course-level bunkers and water. The same list for every hole — which of them belong
   * in this hole's picture is worked out by `holePlan` from the frame it builds, not
   * here.
   */
  hazards: CourseShape[];
  /** Course-level cart paths. The same list for every hole — see `CoursePath`. */
  paths: CoursePath[];
  /** Course-level landmarks, likewise. */
  landmarks: Landmark[];
  /**
   * Every other traced hole on the course, for the diagram's faint context layer.
   *
   * Free: the whole map is one cached file, so this is the holes that were fetched
   * alongside the one being played rather than anything extra off the network.
   */
  others: HoleGeo[];
}

/**
 * Everything the scorecard needs to draw one hole.
 *
 * Paths come back with it rather than separately because they are part of the same
 * fetched map and drawing the hole without them would just mean a second round trip for
 * bytes already in hand.
 */
export async function loadHoleGeo(courseId: string, hole: number): Promise<HoleGeometry> {
  const summary = await findMapForCourse(courseId);
  if (!summary) return { geo: null, hazards: [], paths: [], landmarks: [], others: [] };
  const map = await loadCourseMap(summary.mapId, summary.sha);
  return {
    geo: map?.holes.find((h) => h.hole === hole) ?? null,
    hazards: map?.shapes ?? [],
    paths: map?.paths ?? [],
    landmarks: map?.landmarks ?? [],
    others: map?.holes.filter((h) => h.hole !== hole) ?? [],
  };
}

/** Test seam, and used after the builder commits so a reload isn't needed. */
export function clearMapCaches(): void {
  memoryCache.clear();
  manifest = null;
  manifestFetch = null;
}
